import { forwardRef, useEffect, useId, useMemo, useState } from "react";
import { useModuleBEM, useUiStyles } from "@b2bportal/core-themes";
import { CoreUiComponents } from "@b2bportal/core-types";
import {
  InputContainer,
  InputError,
  type InputTextProps as CoreInputTextProps,
} from "@tenant/ui";
import clsx from "clsx";
import defaultStyles from "./InputText.module.scss";

export type InputTextProps = CoreInputTextProps & {
  floatingLabel?: boolean;
};

const COMPONENT_KEY = CoreUiComponents.InputText;

export const InputText = forwardRef<HTMLInputElement, InputTextProps>(
  (
    {
      label,
      startContent,
      endContent,
      error,
      className,
      type = "text",
      floatingLabel = true,
      ...inputProps
    },
    ref
  ) => {
    const styles = useUiStyles(COMPONENT_KEY, defaultStyles);
    const [block, cn] = useModuleBEM(styles, COMPONENT_KEY, {
      error: error != null,
      disabled:
        inputProps.disabled != null && inputProps.disabled
          ? inputProps.disabled
          : false,
    });

    const reactId = useId();

    const id = useMemo(
      () => (inputProps.id != null ? inputProps.id : reactId),
      [inputProps.id, reactId]
    );

    const [value, setValue] = useState(
      inputProps.value ?? inputProps.defaultValue
    );

    useEffect(() => {
      if (inputProps.value !== undefined) {
        setValue(inputProps.value);
      }
    }, [inputProps.value]);

    return (
      <div className={clsx(block, className)}>
        <InputContainer
          className={cn("container")}
          id={id}
          label={label}
          disabled={inputProps.disabled}
          startContent={startContent}
          endContent={endContent}
          hasError={error != null}
          hasValue={value != null && value !== ""}
          floatingLabel={floatingLabel}
        >
          <input
            {...inputProps}
            ref={ref}
            className={cn("input")}
            id={id}
            aria-label={label}
            onChange={(e) => {
              setValue(e.target.value);
              inputProps?.onChange?.(e);
            }}
            type={type}
          />
        </InputContainer>
        <InputError error={error} />
      </div>
    );
  }
);
